import React from 'react';

import { Link, Stack, Typography } from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';

import CopyableField from '~/components/Common/CopyableField';

import PromoLink from '../PromoLink';

import type { PromoByToggleSearch, PromoByToggleSearchResults } from './PromoByToggle';

type PromoByToggleSearchResult = PromoByToggleSearchResults[number];

type Props = {
  search?: PromoByToggleSearch;
};

const PROMO_LOOKUP_PATH = '/marketing/search-promo';
const PROMO_REQUESTS_PATH = '/marketing/promo-requests';
const VOUCHER_LOOKUP_PATH = '/marketing/voucher-users';

function getRelatedPageUrls(entity: PromoByToggleSearch['entity'], result: PromoByToggleSearchResults[number]) {
  const requestsQuery = new URLSearchParams(
    entity === 'promo' ? { codeName: result.name } : { voucherName: result.name },
  );

  if (entity === 'voucher') {
    return {
      lookup: `${VOUCHER_LOOKUP_PATH}?${new URLSearchParams({ voucher_id: result.id })}`,
      requests: `${PROMO_REQUESTS_PATH}?${requestsQuery}`,
    };
  }

  return {
    lookup: `${PROMO_LOOKUP_PATH}?${new URLSearchParams({ name: result.name, page: '1', limit: '10' })}`,
    requests: `${PROMO_REQUESTS_PATH}?${requestsQuery}`,
  };
}

/** Displays results returned by {@link PromoByToggle}; it is deliberately separate so callers can place it as needed. */
export default function PromoByToggleResults({ search }: Props) {
  if (!search) {
    return null;
  }

  const { entity, results } = search;

  if (results.length === 0) {
    return (
      <Typography role="status" aria-live="polite">
        No matching {entity ?? 'promo or voucher'} results found.
      </Typography>
    );
  }

  const columns: Array<GridColDef<PromoByToggleSearchResult>> = [
    {
      field: 'name',
      headerName: entity === 'voucher' ? 'Voucher' : 'Promo',
      flex: 1,
      minWidth: 240,
      renderCell: (params) =>
        entity === 'promo' ? (
          <PromoLink promoId={params.row.id} title={params.row.name} copyValue={params.row.name} />
        ) : (
          <CopyableField
            value={params.row.name}
            label={
              <Link href={`/marketing/vouchers/${encodeURIComponent(params.row.id)}`} target="_blank" rel="noreferrer">
                {params.row.name}
              </Link>
            }
          />
        ),
    },
    {
      field: 'id',
      headerName: 'ID',
      flex: 1,
      minWidth: 280,
    },
    {
      field: 'relatedPages',
      headerName: 'Related pages',
      sortable: false,
      filterable: false,
      minWidth: 170,
      renderCell: (params) => {
        const relatedPageUrls = getRelatedPageUrls(entity, params.row);
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            <Link href={relatedPageUrls.lookup} target="_blank" rel="noreferrer">
              Lookup
            </Link>
            <Link href={relatedPageUrls.requests} target="_blank" rel="noreferrer">
              Requests
            </Link>
          </Stack>
        );
      },
    },
  ];

  return (
    <DataGrid
      aria-label={`${entity} toggle search results`}
      columns={columns}
      rows={results}
      disableColumnMenu
      disableRowSelectionOnClick
      hideFooter
      autoHeight
    />
  );
}
